import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { monotonicFactory } from 'ulid';

import i18n from '@/app/i18n/i18n';
import { API_PATHS, API_URLS } from '@/config/apiUrls';
import { RUNTIME_MODE } from '@/config/envConfig';
import { logout, setToken } from '@/features/auth/authSlice';
import { contents } from '@/utils/core/constants';
import { useCookie } from '@/utils/useCookie';

import type { RootState } from '@/app/store/store';
import type {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';

export interface ApiExtraOptions {
    /** Public auth exchanges must not inherit a previous user's credentials. */
    skipAuth?: boolean;
    /** Public requests and refresh itself must never trigger recursive refresh. */
    skipReauth?: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

const readRefreshToken = (value: unknown): string | undefined => {
    if (!isRecord(value)) return undefined;
    const token =
        Array.isArray(value.data) && isRecord(value.data[0]) ? value.data[0].token : value.token;
    return typeof token === 'string' && token.length > 0 ? token : undefined;
};

const monotonicUlid = monotonicFactory();

// Mutex to prevent multiple simultaneous token refresh network requests
const mutex: Mutex = new Mutex();

const baseQueryWithAuth = fetchBaseQuery({
    baseUrl: contents.BASE_URL,
    timeout: contents.API_TIMEOUT,
    prepareHeaders: (headers, { getState, extraOptions, arg }) => {
        const state = getState() as RootState;
        if (!(extraOptions as ApiExtraOptions | undefined)?.skipAuth) {
            const token = state.authStore.token || useCookie.getCookie('authToken');
            if (token && !headers.has('Authorization')) {
                headers.set('Authorization', `Bearer ${token}`);
            }
        }

        // Let the browser choose the boundary/content type for binary bodies.
        const body = typeof arg === 'string' ? undefined : arg.body;
        const isBrowserBody =
            body instanceof FormData ||
            body instanceof Blob ||
            body instanceof ArrayBuffer ||
            ArrayBuffer.isView(body) ||
            body instanceof URLSearchParams;
        if (!headers.has('Content-Type') && !isBrowserBody) {
            headers.set('Content-Type', 'application/json');
        }

        if (!headers.has('Accept')) headers.set('Accept', 'application/json');
        headers.set('X-Request-ID', monotonicUlid());
        headers.set('X-Country', state.servicePointProfile.profile?.ctryCd ?? '');
        headers.set('X-Service-Point', state.servicePointProfile.profile?.srvPntFcId ?? '');

        return headers;
    },
});

/**
 *  Base query with global error logs, i18n support, and thread-safe token refresh
 */
const baseQueryWithErrorHandling: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    ApiExtraOptions,
    FetchBaseQueryMeta
> = async (args, api, extraOptions = {}) => {
    // Force incoming calls to wait if another active request thread is currently refreshing tokens
    if (!extraOptions.skipReauth) await mutex.waitForUnlock();

    let result = await baseQueryWithAuth(args, api, extraOptions);

    // Extract endpoint location path string for interceptor evaluation lookaheads
    const targetUrl = typeof args === 'string' ? args : args.url;

    // Intercept 401 errors - Trigger automatic token refresh
    // Safeguard condition: Skip processing if the failing network call IS the token refresh endpoint itself
    const isRefreshRequest = targetUrl
        .split(/[?#]/)[0]
        .replace(/\/+$/, '')
        .endsWith(API_PATHS.sso.refreshUrl.replace(/\/+$/, ''));
    if (result.error?.status === 401 && !extraOptions.skipReauth && !isRefreshRequest) {
        if (!mutex.isLocked()) {
            const release = await mutex.acquire();

            try {
                const state = api.getState() as RootState;
                const currentToken = state.authStore.token || useCookie.getCookie('authToken');

                if (!currentToken) {
                    console.warn(
                        '[RTK Query] No authentication token to refresh, logging session out',
                    );
                    api.dispatch(logout());
                    return result;
                }

                console.log(
                    '[RTK Query] Authentication token expired, invoking refresh sequence...',
                );

                // Attempt to request a new session signature from single sign-on services
                const refreshResult = await baseQueryWithAuth(
                    {
                        url: API_URLS.sso.refreshUrl,
                        method: 'POST',
                        body: { token: currentToken },
                    },
                    api,
                    { skipAuth: true, skipReauth: true },
                );

                const refreshedToken = readRefreshToken(refreshResult.data);
                if (refreshedToken) {
                    // Commit new authorization details to application storage architecture
                    api.dispatch(setToken(refreshedToken));
                    console.log('[RTK Query] Token refresh verified, re-executing blocked request');

                    // Retry original action now that credentials have been cleared and updated
                    result = await baseQueryWithAuth(args, api, extraOptions);
                } else {
                    console.error(
                        '[RTK Query] Token validation sequence failed, clearing session logs',
                    );
                    api.dispatch(logout());
                }
            } finally {
                release();
            }
        } else {
            // Concurrent threads queue up patiently until the running locker thread completes execution
            await mutex.waitForUnlock();
            result = await baseQueryWithAuth(args, api, extraOptions);
        }
    }

    // Process exceptions, validation logging, and string localization rules
    if (result.error) {
        const error = result.error;

        if (RUNTIME_MODE === 'development') {
            console.error('API Error Exception Captured:', {
                status: error.status,
                data: error.data,
                url: targetUrl,
            });
        }

        if (error.status === 401) {
            console.warn('Unauthorized - Token operations expired or invalid validation criteria.');
        } else if (error.status === 403) {
            console.warn('Forbidden - Active credentials lack administrative system access.');
        } else if (error.status === 'FETCH_ERROR') {
            console.error(
                'Network error - Handshake failed, verify client internet connection links.',
            );
        } else if (error.status === 'TIMEOUT_ERROR') {
            console.error(
                'Request timeout - Infrastructure node exceeded maximum handling constraints.',
            );
        }

        // Apply localization transformation metrics across microservice translation keys
        const data = error.data;
        if (
            typeof error.status === 'number' &&
            isRecord(data) &&
            isRecord(data.error) &&
            typeof data.error.msg === 'string'
        ) {
            const interpolations = Array.isArray(data.error.msgParams)
                ? Object.fromEntries(
                      data.error.msgParams.map((value: unknown, index: number) => [index, value]),
                  )
                : {};
            result = {
                ...result,
                error: {
                    ...error,
                    data: {
                        ...data,
                        error: { ...data.error, msg: i18n.t(data.error.msg, interpolations) },
                    },
                },
            };
        }
    }

    return result;
};

/**
 * Enhanced production base query wrapping automated network backoff retries
 */
const baseQueryWithRetry = retry(baseQueryWithErrorHandling, {
    // Preserve existing behavior: do not replay non-idempotent mutations.
    maxRetries: 0,
    backoff: (attempt) => {
        return new Promise((resolve) => {
            // Exponential sequence delays: 1s -> 2s -> 4s protecting gateway routers
            setTimeout(resolve, Math.min(1000 * 2 ** attempt, 4000));
        });
    },
});

/**
 * Centralized RTK Query Base API Interface Core Configuration
 */
const baseApi = createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithRetry,
    // Pre-registered system-wide tags for instant microservice cache management mapping
    tagTypes: [
        'User',
        'Customer',
        'Shipment',
        'Quotation',
        'Configuration',
        'ServicePoint',
        'Product',
        'Package',
        'Payment',
        'Consent',
        'Inventory',
        'Address',
    ],
    keepUnusedDataFor: 30, // Discard dead cache allocations after 30 seconds of inactivity
    refetchOnMountOrArgChange: 30, // Force background validation re-fetch if data is older than 30s
    refetchOnFocus: false, // Disable re-fetching upon user tab window switches
    refetchOnReconnect: true, // Automatically pull tracking changes when internet links restore
    endpoints: () => ({}), // Empty entry registry; downstream features inject modules via split endpoints
});

export const {
    util: { invalidateTags, resetApiState },
} = baseApi;

export default baseApi;
